import { Toggle } from '@/components/inputs';
import { Loadable } from '@/components/loaders';
import { useWorkspace } from '@/features/workspaces/hooks';
import { CustomConnectorDto } from '@/models/connectors';
import { useCreateConnectorInstanceMutation, useDeleteConnectorInstanceMutation } from '@/services/endpoints/workspace-connectors';
import { useCompanyValues } from '@/providers';
import { AUTO_SUBMIT_AUTH_FORM_IDS } from '../connectorFormConfig';
import { useAuth0 } from '@auth0/auth0-react';
import { getDefaultFormState } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { ChangeEvent, FC, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomConnectorAuthModal } from './CustomConnectorAuthModal';
import { CustomConnectorDeauthModal } from './CustomConnectorDeauthModal';

type CustomConnectorConnectionToggleProps = {
  connector: CustomConnectorDto;
  deauthenticateDisabled?: boolean;
  onChangeConnectionInProgress?: (connector: CustomConnectorDto | null) => void;
};

export const CustomConnectorConnectionToggle: FC<CustomConnectorConnectionToggleProps> = ({
  connector,
  deauthenticateDisabled = true,
  onChangeConnectionInProgress,
}) => {
  const { t } = useTranslation();
  const { id: workspaceId, name: workspaceName } = useWorkspace();
  const { currentLanguage: lang } = useCompanyValues();
  const { user } = useAuth0();
  const [deleteConnectorInstance, { isLoading: isDeleting }] = useDeleteConnectorInstanceMutation();
  const [createInstance] = useCreateConnectorInstanceMutation();

  const isConfigured = connector.isAuthenticated;
  const [connectionInProgress, setConnectionInProgress] = useState(false);

  const [isAuthModalShown, toggleAuthModal] = useReducer((state) => !state, false);
  const [isDeauthModalShown, toggleDeauthModal] = useReducer((state) => !state, false);

  const formId = connector.authConfiguration?.configurations?.[0]?.elements?.auth?.reactJsonSchemaForm?.formId;

  const handleDirectConnect = async () => {
    connectorConnectionInProgress(true);
    const authConfig = connector.authConfiguration?.configurations?.[0]?.elements?.auth?.reactJsonSchemaForm;
    const schema = authConfig?.schema ?? {};
    const params = getDefaultFormState(validator, schema, undefined, schema) ?? {};
    params.username = user?.email;
    params.workspaceId = workspaceId;
    params.workspace = workspaceName;

    try {
      await createInstance({
        lang,
        workspaceId,
        connectorId: connector.id,
        name: `${connector.name} Connection`,
        authParams: params,
      }).unwrap();
      onChangeConnectionInProgress?.(connector);
    } catch (error) {
      console.error('Failed to create connector instance:', error);
    } finally {
      connectorConnectionInProgress(false);
    }
  };

  const handleConnectorConnection = ($event: ChangeEvent<HTMLInputElement>) => {
    const { checked } = $event.target;
    if (checked && !isConfigured) {
      if (AUTO_SUBMIT_AUTH_FORM_IDS.includes(formId)) {
        handleDirectConnect();
      } else {
        toggleAuthModal();
      }
    } else if (!checked && isConfigured) {
      toggleDeauthModal();
    }
  };

  const connectorConnectionInProgress = (status: boolean) => {
    setConnectionInProgress(status);
    onChangeConnectionInProgress?.(status ? connector : null);
  };

  const handleDeauthSuccess = () => {
    toggleDeauthModal();
    onChangeConnectionInProgress?.(connector);
  };

  return (
    <Loadable className='h-5' isLoading={connectionInProgress || isDeleting}>
      <Toggle
        label={t(isConfigured ? 'ALL:destinationConnected' : 'ALL:destinationDisconnected')}
        labelPosition='end'
        labelWrapperClassName='capitalize text-sm text-black-100'
        checked={isConfigured}
        onChange={(e) => handleConnectorConnection(e)}
        disabled={deauthenticateDisabled}
      />
      {isAuthModalShown && (
        <CustomConnectorAuthModal
          connector={connector}
          onClose={toggleAuthModal}
          connectorConnectionInProgress={connectorConnectionInProgress}
          onChangeConnectionInProgress={onChangeConnectionInProgress}
        />
      )}
      {isDeauthModalShown && (
        <CustomConnectorDeauthModal workspaceId={workspaceId} connector={connector} onSuccess={handleDeauthSuccess} onClose={toggleDeauthModal} />
      )}
    </Loadable>
  );
};
